import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { Award } from "lucide-react";
import AOS from "aos";
import "aos/dist/aos.css";

const UiUxDesigning = () => {
  useEffect(() => {
    AOS.init({ duration: 1000 });
  }, []);

  return (
    <div className="bg-gray-100 text-gray-800" style={{ marginTop: "80px" }}>
      <motion.header
        className="bg-blue-900 text-white py-16 text-center shadow-lg rounded-lg"
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <div className="bg-gradient-to-r from-blue-50 to-indigo-100 py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                <span className="text-white">✨ UI/UX Design Services by </span>
                <span className="text-blue-600">Right On Time Services </span>
              </h1>
              <p className="text-white mb-8">
                At Right On Time Services, we believe that exceptional design
                isn't just about looks—it's about creating memorable user
                experiences that drive engagement, boost conversions, and
                support your business goals.
              </p>
            </div>
          </div>
        </div>
      </motion.header>

      {/* About Section */}
      <section className="py-16 bg-white" data-aos="fade-up">
        <h2 className="text-3xl font-bold text-blue-900 text-center">
          💡 Unleash the Power of Exceptional Design
        </h2>
        <p className="mt-4 text-lg max-w-4xl mx-auto">
          With years of hands-on experience in UI/UX design and development, our
          team delivers designs that look stunning and perform flawlessly. We
          stay ahead of the curve, constantly evolving with the latest trends,
          tools, and technologies to bring you cutting-edge, custom-tailored
          digital solutions.
        </p>
        <p className="mt-4 text-lg max-w-4xl mx-auto">
          📍Image Suggestion: Banner-style hero image showcasing modern UI/UX
          work across devices (desktop, tablet, mobile).
        </p>
      </section>

      {/* Services Section */}
      <section className="py-16 bg-gray-100" data-aos="fade-up">
        <h2 className="text-3xl font-bold text-blue-900 text-center">
          🎯 Designing Digital Experiences That Convert
        </h2>
        <p className="mt-4 px-8 max-w-4xl mx-auto">
          We offer a comprehensive range of UI/UX services designed for
          businesses of every size and industry:
        </p>
        <div
          className="max-w-6xl mx-auto grid md:grid-cols-2 lg:grid-cols-2 gap-8 px-8 mt-6"
          style={{ marginTop: "30px" }}
        >
          <motion.div
            className="p-6 bg-white rounded-lg shadow-lg hover:shadow-2xl transition duration-300"
            whileHover={{ scale: 1.05 }}
            data-aos="fade-up"
          >
            <h3 className="text-xl font-bold text-blue-900">
              🔍 User Research & Behavior Analysis
            </h3>
            <p className="mt-2">
              We dig deep to understand your audience. From developing personas
              and mapping user journeys to conducting usability tests, we ensure
              your design speaks directly to your users’ needs.
            </p>
            <p className="mt-1">
              📍Image Suggestion: Infographic of a user journey or persona map.
            </p>
          </motion.div>

          <motion.div
            className="p-6 bg-white rounded-lg shadow-lg hover:shadow-2xl transition duration-300"
            whileHover={{ scale: 1.05 }}
            data-aos="fade-up"
          >
            <h3 className="text-xl font-bold text-blue-900">
              🧭 Strategic Information Architecture
            </h3>
            <p className="mt-2">
              We organize your content for clarity and ease. Our approach
              includes creating intuitive navigation structures, logical site
              maps, and seamless content organization.
            </p>
          </motion.div>
          <motion.div
            className="p-6 bg-white rounded-lg shadow-lg hover:shadow-2xl transition duration-300"
            whileHover={{ scale: 1.05 }}
            data-aos="fade-up"
          >
            <h3 className="text-xl font-bold text-blue-900">
              🖼️ Wireframes that Bring Ideas to Life
            </h3>
            <p className="mt-2">
              We sketch out every element before the build. Our low and
              high-fidelity wireframes capture both functionality and layout,
              ensuring design clarity from the start.
            </p>
            <p className="mt-1">
              📍Image Suggestion: Side-by-side wireframe and final product
              comparison.
            </p>
          </motion.div>
          <motion.div
            className="p-6 bg-white rounded-lg shadow-lg hover:shadow-2xl transition duration-300"
            whileHover={{ scale: 1.05 }}
            data-aos="fade-up"
          >
            <h3 className="text-xl font-bold text-blue-900">
              🎨 Visual Design that Captivates
            </h3>
            <p className="mt-2">
              Colors, fonts, buttons, icons — every element matters. We design
              cohesive, on-brand visuals that create immersive digital
              experiences.
            </p>
          </motion.div>
          <motion.div
            className="p-6 bg-white rounded-lg shadow-lg hover:shadow-2xl transition duration-300"
            whileHover={{ scale: 1.05 }}
            data-aos="fade-up"
          >
            <h3 className="text-xl font-bold text-blue-900">
              🧪 Interactive Prototypes
            </h3>
            <p className="mt-2">
              Test it before you build it. Our clickable prototypes let you
              experience your digital product as users would, reducing guesswork
              and revisions down the line.
            </p>
            <p className="mt-1">
              📍Image Suggestion: A video or GIF showing a prototype in action.
            </p>
          </motion.div>
          <motion.div
            className="p-6 bg-white rounded-lg shadow-lg hover:shadow-2xl transition duration-300"
            whileHover={{ scale: 1.05 }}
            data-aos="fade-up"
          >
            <h3 className="text-xl font-bold text-blue-900">
              🛠️ Design Audits for Fast Improvements
            </h3>
            <p className="mt-2">
              Already have a design? We'll analyze it, identify weak spots, and
              offer actionable fixes to elevate your user experience quickly and
              effectively.
            </p>
          </motion.div>
        </div>
      </section>

      <motion.div
        className="p-6 bg-white rounded-lg transition duration-300"
        data-aos="fade-up"
      >
        <section
          className="bg-white py-12 px-6 md:px-20"
          style={{ padding: "40px" }}
        >
          <div className="max-w-5xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
              🎯 Business-Focused, User-Centered Solutions
            </h2>

            <div className="grid md:grid-cols-2 gap-8 text-left">
              <div className="p-6 bg-gray-50 rounded-2xl shadow-md hover:shadow-lg transition duration-300">
                <h3 className="text-xl font-semibold text-blue-600 mb-2">
                  👥 Know Your Users, Win Their Loyalty
                </h3>
                <p className="text-gray-700">
                  Human-centered design means happier users. Increase
                  conversions, engagement, and customer retention by designing
                  with empathy and intent.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-2xl shadow-md hover:shadow-lg transition duration-300">
                <h3 className="text-xl font-semibold text-purple-600 mb-2">
                  💭 Your Vision, Our Canvas
                </h3>
                <p className="text-gray-700">
                  Have a dream design in mind? We bring it to life—pixel by
                  pixel—aligning every element with your business goals and
                  brand personality.
                </p>
              </div>
            </div>
          </div>
        </section>
      </motion.div>

      <motion.div
        className="p-6 bg-white rounded-lg transition duration-300"
        data-aos="fade-up"
      >
        <section className="bg-gray-100 py-16 px-6 md:px-20">
          <div className="max-w-5xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
              🖼️ Our Work Speaks for Itself
            </h2>
            <p className="text-gray-600 mb-12">
              Explore how we’ve helped businesses like yours stand out in the
              digital world:
            </p>

            <div
              className="grid md:grid-cols-3 gap-8 text-left"
              style={{ marginTop: "20px" }}
            >
              <div className="bg-white rounded-2xl shadow-md p-6 hover:shadow-lg transition duration-300">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">
                  Extended Health Services
                </h3>
                <p className="text-gray-600 text-sm">
                  Streamlined healthcare access and improved user interaction
                  with a patient-first web solution.
                </p>
              </div>

              <div className="bg-white rounded-2xl shadow-md p-6 hover:shadow-lg transition duration-300">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">
                  Scribe Align LLC
                </h3>
                <p className="text-gray-600 text-sm">
                  Delivered a secure, scalable system for medical documentation
                  that enhanced efficiency across teams.
                </p>
              </div>

              <div className="bg-white rounded-2xl shadow-md p-6 hover:shadow-lg transition duration-300">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">
                  Clinical Heroes
                </h3>
                <p className="text-gray-600 text-sm">
                  Built a vibrant, responsive platform for clinical recruitment
                  and outreach.
                </p>
              </div>
            </div>
          </div>
        </section>
      </motion.div>

      <motion.div
        className="p-6 bg-white rounded-lg transition duration-300"
        data-aos="fade-up"
      >
        <section className="bg-white py-16 px-6 md:px-20">
          <div className="max-w-5xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-10">
              💼 Why Partner with Right On Time Services?
            </h2>

            <div className="grid md:grid-cols-3 gap-8 text-left">
              <div className="p-6 bg-gray-50 rounded-2xl shadow-md hover:shadow-lg transition duration-300">
                <h3 className="text-xl font-semibold text-blue-600 mb-2">
                  👨‍💻 Seasoned Experts, Proven Results
                </h3>
                <p className="text-gray-700 text-sm">
                  Our diverse team has delivered high-impact UI/UX designs
                  across multiple industries, ensuring every solution meets real
                  business needs.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-2xl shadow-md hover:shadow-lg transition duration-300">
                <h3 className="text-xl font-semibold text-blue-600 mb-2">
                  ✨ Tailored to You
                </h3>
                <p className="text-gray-700 text-sm">
                  From kickoff to launch, we work closely with you to understand
                  your goals, target audience, and brand tone—delivering designs
                  that fit like a glove.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-2xl shadow-md hover:shadow-lg transition duration-300">
                <h3 className="text-xl font-semibold text-blue-600 mb-2">
                  💰 Premium Design Without the Premium Price
                </h3>
                <p className="text-gray-700 text-sm">
                  Quality design shouldn’t break the bank. Our transparent
                  pricing model means no hidden fees—just great value and
                  outstanding service.
                </p>
              </div>
            </div>
          </div>
        </section>
      </motion.div>
      
      <motion.div
        className="p-6 bg-white rounded-lg transition duration-300"
        data-aos="fade-up"
      >
        <section className="bg-gradient-to-br from-blue-50 to-purple-100 py-16 px-6 md:px-20">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
              🚀 Let's Build Something Beautiful Together
            </h2>
            <p className="text-gray-700 mb-8">
              Your digital product deserves to shine. Contact Right On Time
              Services today to discover how our UI/UX design expertise can
              transform your online presence.
            </p>

            <p className="text-gray-700 mb-8" style={{ fontWeight: "bold" }}>
              📍Image Suggestion: A final CTA section with a team photo, a
              contact button, and a glowing testimonial quote.
            </p>
          </div>
        </section>
      </motion.div>

    </div>
  );
};

export default UiUxDesigning;
